import { Card, CardActions, CardContent, Box, Button, Typography } from "@wealthx/ui";
import React from "react";

import PlayArrowIcon from "@mui/icons-material/PlayArrow";

import { BrowserPlan, ActionStatus } from "../../api-services/ai-chat";
import PlanActionItem from "../PlanActionItem";
import { UI_CONSTANTS, ACTION_STATUS } from "../../commons/ai-chat.constants";

import "./browser-plans-list.scss";

interface BrowserPlansListProps {
  plans: BrowserPlan[];
  onPlanClick?: (plan: BrowserPlan, messageIndex: number) => void;
  actionStatuses?: Record<number, Record<string, Record<string, ActionStatus>>>;
  executingPlans?: Set<string>;
  messageIndex: number;
}

const BrowserPlansList = ({
  plans,
  onPlanClick,
  actionStatuses = {},
  executingPlans = new Set(),
  messageIndex,
}: BrowserPlansListProps) => {
  if (!plans || plans.length === 0) return null;

  const handleClick = (plan: BrowserPlan) => {
    // Prevent click if plan is already executing
    if (executingPlans.has(plan.id)) {
      return;
    }
    if (onPlanClick) {
      onPlanClick(plan, messageIndex);
    }
  };

  return (
    <Box className="chats-page__browser-plans">
      {plans.map((plan) => {
        const planActionStatuses = actionStatuses[messageIndex]?.[plan.id] || {};
        const isExecuting = executingPlans.has(plan.id);
        return (
          <Card
            key={plan.id}
            className="chats-page__browser-plan-card"
            sx={{
              border: "1px solid #e0e0e0",
              boxShadow: "none",
              borderRadius: "12px", // Softer corners
              overflow: "visible", // Allow button focus ring if needed
            }}
          >
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ padding: "6px 6px 0 6px" }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    marginBottom: 0.5,
                    color: "text.primary",
                  }}
                >
                  {plan.name}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    marginBottom: 1.5,
                    opacity: 0.9,
                  }}
                >
                  {plan.description}
                </Typography>
              </Box>

              {plan.actions && plan.actions.length > 0 && (
                <Box
                  className="chats-page__browser-plan-actions-list"
                  sx={{ marginBottom: "12px", marginX: "6px" }}
                >
                  {plan.actions.map((action) => {
                    const status = (planActionStatuses[action.id] ||
                      ACTION_STATUS.PENDING) as ActionStatus;
                    return <PlanActionItem key={action.id} action={action} status={status} />;
                  })}
                </Box>
              )}
            </CardContent>

            <CardActions
              sx={{
                padding: "8px 6px 12px 6px",
                justifyContent: "flex-end",
                gap: 1,
              }}
            >
              <Button
                variant="contained"
                size="small"
                startIcon={<PlayArrowIcon fontSize="small" />}
                onClick={() => handleClick(plan)}
                disabled={isExecuting}
                sx={{
                  backgroundColor: "primary.main",
                  textTransform: "none",
                  fontWeight: 600,
                  boxShadow: "none",
                  borderRadius: "20px", // Pill button
                  padding: "6px 20px",
                  "&:hover": {
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    backgroundColor: "primary.dark",
                  },
                }}
              >
                {isExecuting ? "Running..." : "Run Plan"}
              </Button>
            </CardActions>
          </Card>
        );
      })}
    </Box>
  );
};

export default BrowserPlansList;
